/**
 * useTransactionInsightWorkbench — [T249-PY]
 *
 * Dedicated hook that fetches the Transaction Insight Workbench payload
 * for the `/transactions` page. The full-variant Trends tab delegates
 * to the `TransactionInsightWorkbenchSkin`, which expects a typed
 * `TransactionInsightWorkbenchPayload` (timeline / summary / drivers /
 * table). The backend resolver is wired into the `RESOLVER_REGISTRY` and
 * exposed via `POST /api/v1/insights/transaction-workbench` (see
 * `backend/src/routes/insights.ts` + `backend/src/handlers/insightsHandler.ts`).
 *
 * Why not `useWidgetData`?
 *   `useWidgetData` is a dashboard-bound fetcher (it routes through
 *   `POST /dashboards/:id/data`, which requires the widget to be persisted
 *   on the user's default dashboard). The `/transactions` page has no
 *   dashboard context, so we use a dedicated endpoint that calls the
 *   same resolver without that constraint.
 *
 * Design (mirrors `useWidgetData`):
 *   1. AbortController on unmount — prevents setState on unmounted tree.
 *   2. 60 s TTL cache via `useInsightsCache` so toggling Trends tab
 *      back and forth doesn't trigger a refetch storm.
 *   3. `refetch()` callback that re-runs the fetch.
 *   4. Auto-builds the request body with the resolver's default config
 *      (dimension: 'category', measure: 'netSpend', granularity: 'auto',
 *      comparison: 'none', topN: 6) — callers override by passing a
 *      `TransactionInsightWorkbenchConfig` to the hook.
 *   5. Cancels in-flight fetches when the filter set changes mid-flight.
 *
 * [T252-PY] Workbench now respects floating-bar timerange — re-fetches
 * when currentPeriodDays changes:
 *   • `filterSet` deps on `stableStringify(config)`, NOT `[config]`. JS
 *     primitive strings compare by value (`Object.is('a','a') === true`),
 *     so the memo holds a stable object reference across renders that
 *     carry the same payload — and only re-fires when the actual content
 *     changes (e.g. when the floating-bar period pill switches 7D→30D).
 *   • The POST body now carries `kind` so the backend resolver's
 *     discriminated schema (`transactionInsightWorkbenchParamsSchema`,
 *     which requires `kind: 'transaction-insight-workbench'`) accepts
 *     the request and honours the supplied `days` / `dateRange`. The
 *     HTTP body validator now lists `kind` as an optional allow-listed
 *     field (see `backend/src/validators/insightsValidator.ts`).
 *
 * @module hooks/useTransactionInsightWorkbench
 * @see backend/src/handlers/insightsHandler.ts
 * @see backend/src/services/resolvers/transactionInsightWorkbench.ts
 * @see .omo/plans/T248-PY-insights-revamp.md §Theme A1
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '@/services/api';
import { stableStringify, useInsightsCache } from '@/hooks/useInsightsCache';
import type {
  TransactionInsightWorkbenchConfig,
  TransactionInsightWorkbenchPayload,
} from '@/lib/resolverContracts';

// ── Constants ───────────────────────────────────────────────────────────────

/** Backend entrypoint for the workbench resolver (T249-PY). */
const WORKBENCH_ENDPOINT = '/insights/transaction-workbench';

/** Default config — mirrors the resolver's `defaultParams()` in
 *  `backend/src/services/resolvers/transactionInsightWorkbench.ts`. */
const DEFAULT_CONFIG: TransactionInsightWorkbenchConfig = {
  kind: 'transaction-insight-workbench',
  dateRange: 3,
  dimension: 'category',
  measure: 'netSpend',
  granularity: 'auto',
  comparison: 'none',
  topN: 6,
};

// ── Public types ────────────────────────────────────────────────────────────

/**
 * Return shape of `useTransactionInsightWorkbench`. Mirrors the lifecycle
 * of `useWidgetData` but trimmed to the four fields the workbench skin
 * actually consumes (`data`, `loading`, `error`, plus `refetch`).
 */
export interface UseTransactionInsightWorkbenchResult {
  /** Resolved payload — `null` while loading or when an empty payload is returned. */
  data: TransactionInsightWorkbenchPayload | null;
  /** `true` while the initial fetch is in flight. */
  loading: boolean;
  /** Error message when the fetch failed; `null` otherwise. */
  error: string | null;
  /** Manually trigger a refetch (e.g. on dimension/measure change). */
  refetch: () => void;
}

// ── Hook ────────────────────────────────────────────────────────────────────

/**
 * Fetch the Transaction Insight Workbench payload for the current user.
 *
 * @param config - Optional overrides (dimension, measure, granularity,
 *                 comparison, topN, dateRange, accountIds, categoryIds).
 *                 Defaults to the resolver's standard config.
 */
export function useTransactionInsightWorkbench(
  config: TransactionInsightWorkbenchConfig = DEFAULT_CONFIG,
): UseTransactionInsightWorkbenchResult {
  // Stable serialisable filter set — content-addressed via stableStringify.
  // The dependency `[stableStringify(config)]` is a string primitive that
  // compares by value across renders, so the memo holds the SAME reference
  // when the config content is unchanged — preventing the useEffect from
  // re-firing on every parent render. When the floating-bar period pill
  // changes (e.g. 7d → 30d), the string value changes, the memo returns a
  // new reference, and the effect re-runs the network call.
  const filterSet = useMemo(
    () => config,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stableStringify(config)],
  );

  const insightsCache = useInsightsCache<TransactionInsightWorkbenchPayload>();

  const [data, setData] = useState<TransactionInsightWorkbenchPayload | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Ref to drive manual refetch from the returned `refetch` callback.
  const refetchCounterRef = useRef(0);
  const [refetchTick, setRefetchTick] = useState(0);

  const refetch = useCallback(() => {
    refetchCounterRef.current += 1;
    setRefetchTick((n) => n + 1);
  }, []);

  useEffect(() => {
    // [T246-FE-CACHE] Serve from cache when a fresh entry exists.
    const cached = insightsCache.get(filterSet);
    if (cached !== null && !cached.isStale) {
      setData(cached.data);
      setLoading(false);
      setError(null);
      return;
    }

    const controller = new AbortController();
    insightsCache.beginFetch(filterSet, controller);
    setLoading(true);
    setError(null);

    // [T252-PY] Pass the full config (including the `kind` discriminator)
    // so the resolver's strict Zod schema accepts the request. The HTTP
    // body validator now allow-lists `kind` (default
    // `'transaction-insight-workbench'`); without `kind`, the resolver
    // schema's `kind: z.literal(...)` check fails and the request falls
    // back to `defaultParams()` — which silently ignores `days` /
    // `dateRange` and always returns a 90-day window. With `kind`, the
    // supplied `days` propagates end-to-end (e.g. 7D pill →
    // workbench chart shows 7 day-granularity buckets).
    api
      .post<{
        success: boolean;
        data: TransactionInsightWorkbenchPayload;
      }>(WORKBENCH_ENDPOINT, { ...filterSet }, { signal: controller.signal })
      .then((response) => {
        if (controller.signal.aborted) return;
        const payload = response.data?.data ?? null;
        setData(payload);
        setLoading(false);
        setError(null);
        if (payload) {
          insightsCache.set(filterSet, payload);
        }
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        // Axios wraps AbortError in a CanceledError; treat both as a no-op.
        const isAbort =
          err instanceof Error &&
          (err.name === 'CanceledError' || err.name === 'AbortError');
        if (isAbort) return;
        const message =
          err instanceof Error
            ? err.message
            : 'Failed to load transaction insight workbench';
        setError(message);
        setLoading(false);
        setData(null);
      })
      .finally(() => {
        insightsCache.endFetch(filterSet);
      });

    return () => {
      controller.abort('cache:filter-changed');
    };
    // We intentionally depend on `filterSet` (a content-stable memo) +
    // `refetchTick` so the effect re-runs only when the logical filter
    // set changes or the user calls `refetch()`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSet, refetchTick]);

  return { data, loading, error, refetch };
}

export default useTransactionInsightWorkbench;
